import Configs from '@/Configs';

import { hexToRGBA } from '@/common/utils/colorUtils.js';

import { getCompanyBrandFromPreferences } from '@/utils/companyBrand.js';

export const LOGIN_BRAND_CACHE_KEY = 'kincode-login-brand';

export const LOGIN_BRAND_CACHE_VERSION = 1;

export type LoginBrandCache = {
  version: typeof LOGIN_BRAND_CACHE_VERSION;
  companyID: number | null;
  colorHex: string;
  logoUrl: string | null;
  updatedAt: string;
};

export type LoginBrandDisplay = {
  colorHex: string;
  logoUrl: string;
  mainBackgroundStyle: { backgroundImage: string };
  isBranded: boolean;
};

type CompanyPreferencesLike = {
  personalization?: {
    colorHex?: string | null;
    color?: string | null;
    logoLarge?: string | null;
  } | null;
} | null;

/**
 * URLs that can be stored for the unauthenticated login `<img src>`.
 * Includes S3 presigned URLs from `getPublicURLForFile` (rejected by `isSafeDirectImagePreviewUrl`).
 *
 * @param url - Resolved or stored logo URL.
 */
export function isLoginBrandCacheableLogoUrl(url: string | undefined | null): boolean {
  const trimmed = url?.trim() ?? '';
  if (!trimmed) {
    return false;
  }

  if (trimmed.startsWith('blob:')) {
    return false;
  }

  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

/**
 * Resolves a logo URL safe to cache for the unauthenticated login page.
 *
 * @param logoLarge - `personalization.logoLarge` from company preferences.
 * @param resolvedLogoUrl - Already-resolved public URL (e.g. from `getPublicLogoLargeURL`).
 */
export function resolveCacheableLoginLogoUrl(
  logoLarge?: string | null,
  resolvedLogoUrl?: string | null
): string | null {
  const resolved = resolvedLogoUrl?.trim() ?? '';
  if (resolved && isLoginBrandCacheableLogoUrl(resolved)) {
    return resolved;
  }

  const reference = logoLarge?.trim() ?? '';
  if (reference && isLoginBrandCacheableLogoUrl(reference)) {
    return reference;
  }

  return null;
}

function parseCompanyId(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function isValidCacheEntry(value: unknown): value is LoginBrandCache {
  if (!value || typeof value !== 'object') {
    return false;
  }

  const entry = value as Partial<LoginBrandCache>;

  return (
    entry.version === LOGIN_BRAND_CACHE_VERSION &&
    typeof entry.colorHex === 'string' &&
    entry.colorHex.startsWith('#') &&
    (entry.logoUrl === null || typeof entry.logoUrl === 'string') &&
    (entry.companyID === null || typeof entry.companyID === 'number') &&
    typeof entry.updatedAt === 'string'
  );
}

/**
 * Reads persisted login branding from `localStorage`. Returns `null` when missing or invalid.
 */
export function readLoginBrandCache(): LoginBrandCache | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem(LOGIN_BRAND_CACHE_KEY);
    if (!raw) {
      return null;
    }

    const parsed: unknown = JSON.parse(raw);
    return isValidCacheEntry(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * Maps cache (or defaults) to login page display values.
 */
export function getLoginBrandDisplay(cache: LoginBrandCache | null): LoginBrandDisplay {
  const defaultColorHex = Configs.companyColor;
  const colorHex = cache?.colorHex ?? defaultColorHex;
  const logoUrl = cache?.logoUrl ?? Configs.companyLogoLarge;
  const isBranded = cache != null;

  return {
    colorHex,
    logoUrl,
    isBranded,
    mainBackgroundStyle: {
      backgroundImage: `linear-gradient(to bottom, ${hexToRGBA(colorHex, 0.35)}, #ffffff)`,
    },
  };
}

/**
 * Persists login branding after a successful authenticated session.
 * Preserves an existing cached logo when a new logo URL is not yet available.
 *
 * @param companyID - Tenant id from login/profile.
 * @param companyPreferences - Company preferences including personalization.
 * @param resolvedLogoUrl - Optional resolved large-logo URL.
 */
export function persistLoginBrandCache(
  companyID: unknown,
  companyPreferences?: CompanyPreferencesLike,
  resolvedLogoUrl?: string | null
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const { colorHex } = getCompanyBrandFromPreferences(companyPreferences);
  const logoLarge = companyPreferences?.personalization?.logoLarge;
  const nextLogoUrl = resolveCacheableLoginLogoUrl(logoLarge, resolvedLogoUrl);
  const existing = readLoginBrandCache();
  const parsedCompanyId = parseCompanyId(companyID);

  const logoUrl =
    nextLogoUrl ??
    (existing?.companyID === parsedCompanyId ? existing.logoUrl : null) ??
    null;

  const entry: LoginBrandCache = {
    version: LOGIN_BRAND_CACHE_VERSION,
    companyID: parsedCompanyId,
    colorHex,
    logoUrl,
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(LOGIN_BRAND_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Ignore quota / private-mode failures; login must not break.
  }
}

/**
 * Updates only the cached logo URL (e.g. after `getPublicLogoLargeURL` resolves a file id).
 *
 * @param logoUrl - Browser-safe image URL.
 * @param companyID - Optional tenant guard; skips update when cache is for another company.
 */
export function updateLoginBrandCacheLogoUrl(
  logoUrl: string | null | undefined,
  companyID?: number | null
): void {
  const safeUrl = logoUrl?.trim() ?? '';
  if (!safeUrl || !isLoginBrandCacheableLogoUrl(safeUrl)) {
    return;
  }

  const existing = readLoginBrandCache();
  if (!existing) {
    return;
  }

  if (
    companyID != null &&
    existing.companyID != null &&
    existing.companyID !== companyID
  ) {
    return;
  }

  if (typeof window === 'undefined') {
    return;
  }

  const entry: LoginBrandCache = {
    ...existing,
    logoUrl: safeUrl,
    updatedAt: new Date().toISOString(),
  };

  try {
    window.localStorage.setItem(LOGIN_BRAND_CACHE_KEY, JSON.stringify(entry));
  } catch {
    // Ignore storage failures.
  }
}
